import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Star, MousePointerClick, TrendingUp, Calendar, ExternalLink, Mail, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { companyApi } from '../../../lib/api';

export function ReviewsPage() {
  const [reviewStats, setReviewStats] = useState({ 
    total_clicks: 0, 
    monthly_clicks: 0, 
    recent_clicks: [], 
    monthly_stats: {} 
  });
  const [companySettings, setCompanySettings] = useState({ google_review_link: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          companyApi.getReviewStats(),
          companyApi.getProfile()
        ]);
        setReviewStats(statsRes.data);
        setCompanySettings(profileRes.data);
      } catch (error) {
        console.error('Error loading review stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) return <div className="animate-pulse space-y-6"><div className="h-8 bg-muted rounded w-1/3" /><div className="h-48 bg-muted rounded-xl" /></div>;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Suivi des Avis Google</h1>
        <p className="text-muted-foreground mt-1">
          Suivez les clics sur vos demandes d'avis envoyées après chaque intervention
        </p>
      </div>

      {/* Alert if no Google link configured */}
      {!companySettings.google_review_link && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">Lien Google Avis non configuré</p>
              <p className="text-sm text-amber-700">Configurez votre lien dans Paramètres &gt; Compte pour activer le suivi des avis.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/company/account'}>
              Configurer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total clics</p>
                <p className="text-4xl font-bold text-primary">{reviewStats.total_clicks}</p>
                <p className="text-xs text-muted-foreground mt-1">Depuis le début</p>
              </div>
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <MousePointerClick className="w-7 h-7 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Ce mois-ci</p>
                <p className="text-4xl font-bold text-green-700">{reviewStats.monthly_clicks}</p>
                <p className="text-xs text-green-600 mt-1">
                  {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </p>
              </div>
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lien Google</p>
                {companySettings.google_review_link ? (
                  <a 
                    href={companySettings.google_review_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                  >
                    Voir la page <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <p className="text-sm text-amber-600 mt-2">Non configuré</p>
                )}
              </div>
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <Star className="w-7 h-7 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart */}
      {Object.keys(reviewStats.monthly_stats || {}).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Évolution mensuelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {Object.entries(reviewStats.monthly_stats).reverse().map(([month, count]) => {
                const maxCount = Math.max(...Object.values(reviewStats.monthly_stats), 1);
                const height = (count / maxCount) * 100;
                return (
                  <div key={month} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full bg-primary/20 rounded-t-lg transition-all hover:bg-primary/30"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    >
                      <div className="text-center text-xs font-medium py-1">{count}</div>
                    </div>
                    <span className="text-xs text-muted-foreground">{month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Clicks Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Derniers clics</CardTitle>
          <CardDescription>
            Historique des clients ayant cliqué sur le lien "Donner mon avis"
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reviewStats.recent_clicks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MousePointerClick className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Aucun clic enregistré</p>
              <p className="text-sm">Les clics apparaîtront ici après l'envoi des emails post-intervention</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Date du clic</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewStats.recent_clicks.map((click) => (
                  <TableRow key={click.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-medium">{click.client_name || 'Client'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {click.client_email ? (
                        <span className="flex items-center gap-1 text-sm">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          {click.client_email}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{formatDate(click.clicked_at)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{click.source === 'email' ? 'Email' : click.source}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Comment ça fonctionne ?</p>
              <p className="text-sm text-blue-700 mt-1">
                À chaque intervention terminée, un email est automatiquement envoyé au client avec un bouton 
                "Donner mon avis". Quand le client clique, le clic est enregistré ici avant de le rediriger 
                vers votre page Google Avis.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
